import { useState } from "react";
import { Check, Handshake, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { AceiteMultiClinicaDialog } from "@/components/partners/AceiteMultiClinicaDialog";

/**
 * Convites de parceria endereçados a MIM (por email), aguardando aceite.
 *
 * Vive no topo da aba Parceiros porque é aqui que o dentista olha quando o assunto é laboratório —
 * é a mesma prateleira onde os pedidos de conexão enviados já aparecem. A consulta é por email da
 * sessão (espelho do convite de equipe), então a caixa aparece em qualquer organização. Aceitar
 * abre a escolha de clínicas (AceiteMultiClinicaDialog): quem tem várias conecta todas ou escolhe,
 * independente da organização ativa — e clínica já conectada aparece como "Parceria já existe".
 */
export function PendingPartnerInvitesList() {
  const utils = trpc.useUtils();
  const listQuery = trpc.partner.connectInvite.listMine.useQuery();
  const convites = listQuery.data ?? [];
  const [aceitando, setAceitando] = useState<string | null>(null);

  const recusar = trpc.partner.connectInvite.declineMine.useMutation({
    onSuccess: () => {
      toast.success("Convite recusado.");
      utils.partner.connectInvite.listMine.invalidate();
      utils.notification.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao recusar."),
  });

  if (convites.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-brand-200 bg-brand-50/50 dark:border-primary/30 dark:bg-primary/5">
      <div className="border-b border-brand-200/60 px-3 py-2 dark:border-primary/20">
        <p className="text-[12px] font-semibold text-brand-900 dark:text-primary">
          {convites.length === 1
            ? "1 laboratório convidou você pra parceria"
            : `${convites.length} laboratórios convidaram você pra parceria`}
        </p>
      </div>
      <ul className="divide-y divide-brand-200/40 dark:divide-primary/15">
        {convites.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[12px] sm:flex-nowrap">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-100 text-brand-700 dark:bg-primary/15 dark:text-primary">
              <Handshake className="h-3.5 w-3.5" />
            </span>
            <span className="min-w-0 flex-1 truncate font-semibold text-zinc-900 dark:text-foreground">
              {c.organization.name}
            </span>
            <span className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={() => setAceitando(c.id)}
                disabled={recusar.isPending}
                className="inline-flex h-9 items-center gap-1 rounded-md bg-brand-600 px-2.5 text-[11.5px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
              >
                <Check className="h-3.5 w-3.5" />
                Aceitar
              </button>
              <button
                type="button"
                onClick={() => recusar.mutate({ id: c.id })}
                disabled={recusar.isPending}
                className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-[11.5px] font-medium text-zinc-500 transition-colors hover:bg-brand-100 hover:text-red-600 disabled:opacity-50 dark:text-muted-foreground dark:hover:bg-primary/15"
              >
                {recusar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                Recusar
              </button>
            </span>
          </li>
        ))}
      </ul>

      <AceiteMultiClinicaDialog
        aberto={aceitando !== null}
        origem={aceitando ? { tipo: "convite", id: aceitando } : null}
        onFechar={() => setAceitando(null)}
        onSucesso={() => setAceitando(null)}
      />
    </div>
  );
}
